import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SortDirection, SortState, SortActions } from './types';

interface UseSortableColumnsOptions {
  defaultSortBy?: string | null;
  defaultDirection?: SortDirection;
  onSortChange?: (sortBy: string | null, direction: SortDirection) => void;
}

export function useSortableColumns({
  defaultSortBy,
  defaultDirection = 'asc',
  onSortChange,
}: UseSortableColumnsOptions = {}): SortState & SortActions {
  const [searchParams, setSearchParams] = useSearchParams();

  const explicitSortBy = searchParams.get('sortBy');
  const explicitSortDir = searchParams.get('sortDir') as SortDirection | null;
  const hasExplicitSort = !!explicitSortBy;

  const sortBy = explicitSortBy || defaultSortBy || null;
  const sortDirection = explicitSortDir || defaultDirection;

  const handleSort = useCallback(
    (columnId: string) => {
      const newParams = new URLSearchParams(searchParams);

      // If current sorting is only coming from defaults, treat first click specially
      if (!hasExplicitSort) {
        if (columnId === defaultSortBy) {
          const nextDirection: SortDirection = defaultDirection === 'asc' ? 'desc' : 'asc';

          newParams.set('sortBy', columnId);
          newParams.set('sortDir', nextDirection);
        } else {
          newParams.set('sortBy', columnId);
          newParams.set('sortDir', 'asc');
        }

        newParams.set('page', '1');
        setSearchParams(newParams);

        if (onSortChange) {
          onSortChange(
            newParams.get('sortBy'),
            (newParams.get('sortDir') as SortDirection) || defaultDirection
          );
        }

        return;
      }

      if (sortBy === columnId) {
        if (sortDirection === 'asc') {
          newParams.set('sortDir', 'desc');
        } else {
          newParams.delete('sortBy');
          newParams.delete('sortDir');
        }

        newParams.set('page', '1');
      } else {
        newParams.set('sortBy', columnId);
        newParams.set('sortDir', 'asc');
        newParams.set('page', '1');
      }

      setSearchParams(newParams);

      if (onSortChange) {
        const newSortBy = newParams.get('sortBy');
        const newDirection = (newParams.get('sortDir') as SortDirection) || defaultDirection;
        onSortChange(newSortBy, newDirection);
      }
    },
    [
      searchParams,
      setSearchParams,
      hasExplicitSort,
      sortBy,
      sortDirection,
      defaultSortBy,
      defaultDirection,
      onSortChange,
    ]
  );

  const clearSort = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('sortBy');
    newParams.delete('sortDir');
    newParams.set('page', '1');
    setSearchParams(newParams);

    if (onSortChange) {
      onSortChange(null, defaultDirection);
    }
  }, [searchParams, setSearchParams, onSortChange, defaultDirection]);

  return {
    sortBy,
    sortDirection,
    handleSort,
    clearSort,
  };
}

/**
 * Get sort indicator for a column
 */
export function getSortIndicator(
  columnId: string,
  currentSortBy: string | null,
  currentDirection: SortDirection
): '↑' | '↓' | '' {
  if (currentSortBy !== columnId) return '';
  return currentDirection === 'asc' ? '↑' : '↓';
}

/**
 * Check if a column is currently sorted
 */
export function isColumnSorted(columnId: string, currentSortBy: string | null): boolean {
  return currentSortBy === columnId;
}
